/* Shading that makes a mark read as a sphere rather than a disc.
 *
 * A neutral overlay rather than a lightened and darkened version of the mark's own colour, for two
 * reasons. `mark.color` is whatever the caller supplied - an `rgb()` string from the ramp, a hex
 * from `point.fill`, or the ink colour for a component with no value - so there is nothing safe to
 * parse and lighten. And a white-to-black wash over the flat fill leaves the fill itself carrying
 * the encoding: the hue a reader matches against the legend is still exactly the hue that was
 * filled, with light on top of it.
 *
 * The overlay is shaded per pixel by the same `lighting` rig the surface chart uses, against the
 * true normal of a sphere, rather than approximated by a radial gradient. A gradient interpolates
 * linearly between its stops, and a lit sphere does not: the diffuse term falls off as the cosine
 * of the angle to the light, which over the projected disc is steep near the rim and flat through
 * the middle. Ramping it linearly is what reads as an airbrushed circle - the highlight blooms too
 * wide, the terminator arrives too early, and the silhouette has no limb darkening at all. It also
 * cost one gradient per mark per frame, where this is one sprite for the whole chart.
 *
 * Both lights are in play, so the highlight and the terminator sit where the surface chart's do:
 * `KEY_LIGHT` is in *view* axes, so it follows the orbit and its screen direction on the mark is
 * fixed, and `SKY_FILL_LIGHT` is cool and overhead. Only the directions are borrowed, though - the
 * balance between ambient and diffuse is the mark's own, for the reason on {@link SPRITE_LIGHTING}.
 */

import {
  DEFAULT_LIGHTING,
  KEY_LIGHT,
  SKY_FILL_LIGHT,
  accumulateShading,
  resolveLights,
  type LightingModel,
  type Vector3,
} from './lighting';
import type { CameraBasis } from '../geometry/projection';
import { DEFAULT_POINTS_LOOK, type MarkShadingLook } from './pointsLook';

/**
 * Below this the shading is a smudge rather than a form.
 *
 * This used to say the scatter stays flat, and that was never true of the code. A scatter's marks
 * are not a few pixels across: `toDiameter` takes the `!showSizeVariation` arm and returns
 * `MINIMUM_DIAMETER * 1.5` = 12, foreshortened by the projection. Measured over the plane at the
 * default camera that is 9.6px to 16.0px, so this threshold sits *inside* the scatter's range
 * rather than above it and **98% of a scatter's marks are shaded**.
 *
 * Kept where it is, and the claim corrected instead. Raising it past the scatter would take the
 * bottom of the bubble ramp with it - the smallest bubble is 8px before foreshortening - and the
 * ramp is what the shading is for. What this now means is only what it says: a mark too small to
 * carry a form. Whether a *uniform* scatter should be modelled at all is a question about the
 * chart rather than about a number, and nothing here decides it.
 */
export const SHADED_MIN_DIAMETER = 10;

/**
 * The sprite is drawn at this size and scaled to each mark.
 *
 * The shading is the same for every mark - one light rig, one sphere, only the radius differs - so
 * it is computed once and blitted.
 *
 * Whether 128 is enough is a question about *device* pixels, not CSS ones, and an earlier version
 * of this comment answered the wrong one. `useChartCanvas` sets the transform to
 * `devicePixelRatio`, so the destination is `diameter x ratio`. The default `maximumDiameter` of 34
 * with the most foreshortening the projector can apply - `1 / (1 - 0.22 * depth)`, about 1.61 at
 * the far corner - is roughly 55 CSS px: 110 device px at 2x, which is still a reduction, and 164
 * at 3x, which is not. `maximumDiameter` is a caller-supplied prop with no ceiling, so a larger one
 * scales that further.
 *
 * Left at 128 rather than raised. Quadrupling it costs 4.6 ms of bake against 2.0 ms, once per
 * document but on the first paint, to improve a case that degrades gently anyway: the sprite is a
 * smooth gradient whose only sharp feature is the limb, the separation ring covers most of that,
 * and `renderPoints3d` now asks for high-quality resampling. Softness is the failure mode here, not
 * aliasing.
 */
export const SPRITE_SIZE = 128;

/**
 * How much of the lit sphere reaches the fill.
 *
 * The other dial is {@link SPRITE_AMBIENT}, and they do different jobs: the ambient decides how
 * much range the light has across the sphere, and this decides how much of that range is spent as
 * ink on top of the datum. Held below 1 so the overlay is modelling and not repainting.
 */
const OVERLAY_STRENGTH = DEFAULT_POINTS_LOOK.shading.strength;

/** The camera the sprite is baked against: straight down the view axis, y up. */
const SPRITE_BASIS: CameraBasis = {
  right: [1, 0, 0],
  up: [0, 1, 0],
  forward: [0, 0, 1],
};

/**
 * How dark the turned-away side is allowed to get, as a fraction of the facing side.
 *
 * `DEFAULT_LIGHTING` holds this at 0.62, which is right for the surface: a facet's colour is its
 * height read off a ramp, so light may only shade it, never move it far enough that the legend
 * stops being true of it. It leaves too little range for a sphere. Measured against the rig as
 * shipped, the overlay spanned 4% coverage at the middle to 20% at the limb - a band that narrow
 * models a slightly soft disc, not a ball.
 *
 * A mark can afford more because it is not a ramp readout across its own area. Its whole surface
 * is one datum, the reader matches it by hue rather than by value, and the middle - where the eye
 * lands to do that - is the part this leaves alone.
 */
const SPRITE_AMBIENT = DEFAULT_POINTS_LOOK.shading.ambient;

/**
 * The rig the sprite is lit by: the shipped directions, rebalanced by {@link SPRITE_AMBIENT}.
 *
 * The diffuse terms are scaled to keep `ambient + diffuse` summing to 1, which is the invariant
 * that makes a facet turned toward the light sit at its own colour rather than somewhere brighter.
 * Moving the ambient down without it would darken the middle of every mark and shift the hue the
 * legend names.
 */
const toSpriteLighting = ({
  ambient,
  gloss,
  shininess,
}: MarkShadingLook): LightingModel => {
  const diffuseTotal = SKY_FILL_LIGHT.diffuse + KEY_LIGHT.diffuse;
  const scale = (1 - ambient) / diffuseTotal;

  return {
    ...DEFAULT_LIGHTING,
    ambient,
    shininess,
    lights: [
      {
        ...SKY_FILL_LIGHT,
        diffuse: SKY_FILL_LIGHT.diffuse * scale,
        specular: SKY_FILL_LIGHT.specular * gloss,
      },
      {
        ...KEY_LIGHT,
        diffuse: KEY_LIGHT.diffuse * scale,
        specular: KEY_LIGHT.specular * gloss,
      },
    ],
  };
};

const SPRITE_LIGHTING: LightingModel = toSpriteLighting({
  ...DEFAULT_POINTS_LOOK.shading,
  ambient: SPRITE_AMBIENT,
  shininess: DEFAULT_LIGHTING.shininess,
});

/**
 * The outward normal of a unit sphere at a point on its projected disc, or `null` off the disc.
 *
 * `v` is in canvas coordinates, which grow downward, so the y of the normal is negated: a point
 * near the top of the mark faces up in the light's frame.
 */
export const toSphereNormal = (u: number, v: number): Vector3 | null => {
  const squared = u * u + v * v;
  if (squared > 1) return null;
  return [u, -v, Math.sqrt(1 - squared)];
};

export type Overlay = {
  /** How much of the overlay covers the fill, 0 to 1. */
  alpha: number;
  /** 0 for a black overlay, 1 for a white one. */
  whiteness: number;
};

/** Fully transparent, for the pixels outside the disc. */
const CLEAR: Overlay = { alpha: 0, whiteness: 0 };

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

/**
 * The overlay at one point of the disc.
 *
 * The rig answers a multiplier and an additive highlight, and canvas compositing offers neither
 * directly - `source-over` gives `fill·(1-a) + overlay·a`. Writing the darkening as black and the
 * highlight as white and summing their coverages lands on `fill·(intensity - highlight) +
 * highlight`, which differs from the rig's `fill·intensity + highlight` by `fill·highlight`. At
 * these highlight strengths that is under a value step, and it is the same approximation any
 * overlay-based shading makes.
 */
export const toSphereOverlay = (
  u: number,
  v: number,
  lights = resolveLights(SPRITE_LIGHTING, SPRITE_BASIS),
  lighting: LightingModel = SPRITE_LIGHTING,
  strength: number = OVERLAY_STRENGTH
): Overlay => {
  const normal = toSphereNormal(u, v);
  if (!normal) return CLEAR;

  const intensity: [number, number, number] = [0, 0, 0];
  const highlight: [number, number, number] = [0, 0, 0];
  accumulateShading(
    normal,
    lights,
    lighting.ambient,
    lighting.shininess,
    intensity,
    highlight,
    0
  );

  /* One channel of each: the overlay is neutral by construction, so a coloured light contributes
     its brightness and not its hue. Mixing the rig's tint in here would shift the fill away from
     the value the legend names. */
  const lit = (intensity[0] + intensity[1] + intensity[2]) / 3;
  const gloss = (highlight[0] + highlight[1] + highlight[2]) / 3;

  const darkening = clamp01(1 - lit) * strength;
  const brightening = clamp01(gloss) * strength;
  const alpha = clamp01(darkening + brightening);

  return {
    alpha,
    whiteness: alpha === 0 ? 0 : brightening / alpha,
  };
};

/**
 * The sprite's pixels: RGBA, premultiplied by nothing, ready for `putImageData`.
 *
 * Separated from the canvas so the arithmetic can be tested without one.
 */
export const toSpritePixels = (
  size: number,
  shading: MarkShadingLook = DEFAULT_POINTS_LOOK.shading
): Uint8ClampedArray => {
  const pixels = new Uint8ClampedArray(size * size * 4);
  const lighting = toSpriteLighting(shading);
  const lights = resolveLights(lighting, SPRITE_BASIS);
  const half = size / 2;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      /* Pixel centres, so the disc is symmetric and its edge lands between samples. */
      const u = (x + 0.5 - half) / half;
      const v = (y + 0.5 - half) / half;
      const { alpha, whiteness } = toSphereOverlay(
        u,
        v,
        lights,
        lighting,
        shading.strength
      );

      const channel = Math.round(whiteness * 255);
      const offset = (y * size + x) * 4;
      pixels[offset] = channel;
      pixels[offset + 1] = channel;
      pixels[offset + 2] = channel;
      pixels[offset + 3] = Math.round(alpha * 255);
    }
  }

  return pixels;
};

/** One sprite per distinct shading, so a chart that never changes its look bakes exactly one. */
const spritesByShading = new Map<string, HTMLCanvasElement>();

const toShadingKey = ({
  ambient,
  strength,
  gloss,
  shininess,
}: MarkShadingLook): string => `${ambient}|${strength}|${gloss}|${shininess}`;

/**
 * The shading sprite, built once.
 *
 * Cached at module scope because it depends on nothing that varies: the key light is in view axes
 * and the sphere is always a sphere, so the picture is identical for every mark of every chart in
 * the document, at every orbit.
 */
export const getSphereSprite = (
  shading: MarkShadingLook = DEFAULT_POINTS_LOOK.shading
): HTMLCanvasElement | null => {
  const key = toShadingKey(shading);
  const existing = spritesByShading.get(key);
  if (existing) return existing;
  if (typeof document === 'undefined') return null;

  const canvas = document.createElement('canvas');
  canvas.width = SPRITE_SIZE;
  canvas.height = SPRITE_SIZE;

  const context = canvas.getContext('2d');
  if (!context) return null;

  const image = context.createImageData(SPRITE_SIZE, SPRITE_SIZE);
  image.data.set(toSpritePixels(SPRITE_SIZE, shading));
  context.putImageData(image, 0, 0);

  spritesByShading.set(key, canvas);
  return canvas;
};

/**
 * The diameter at which the modelling reaches full strength.
 *
 * There is a band rather than a line because the test is on the *foreshortened* diameter, which
 * changes as the plot turns. A mark near the threshold would otherwise flip between no overlay and
 * a whole one between two frames of an orbit - measured, the smallest mark on the size ramp runs
 * 6.4px to 11.8px across the plane at the default camera, so it crosses in the middle. The band
 * turns that step into a fade nobody can see happening.
 */
const SHADING_FADE_BAND = 4;

/**
 * How much of the modelling a mark of this diameter gets, 0 to 1.
 *
 * `NaN` answers 0: `toDiameter` multiplies by the projection scale, and a degenerate camera has
 * produced a `NaN` there before - which would otherwise reach `drawImage` as an alpha.
 */
export const shadingStrengthAbove =
  (minimumDiameter: number) =>
  (diameter: number): number => {
    if (!(diameter > minimumDiameter)) return 0;

    return clamp01((diameter - minimumDiameter) / SHADING_FADE_BAND);
  };

export const toShadingStrength = shadingStrengthAbove(SHADED_MIN_DIAMETER);
